import React, { useState, useEffect, useMemo } from "react";
import { Check, ArrowUpDown, Bell } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { useUser } from "../contexts/UserContext"; // Add this import
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { Input } from "./ui/input";

const AppListComponents = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [apps, setApps] = useState([]);
  const [editedApps, setEditedApps] = useState({});
  const [newAppName, setNewAppName] = useState("");
  const [newWebsite, setNewWebsite] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const itemsPerPage = 15;
  const [sortConfig, setSortConfig] = useState({
    key: "currentAmount",
    direction: "desc",
  });
  const { user } = useUser(); // Add user context
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [appToArchive, setAppToArchive] = useState(null);

  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [alertType, setAlertType] = useState("scheduled");
  const [scheduledTime, setScheduledTime] = useState("");
  const [intervalDuration, setIntervalDuration] = useState("");

  // Fetch apps and transactions
  const fetchData = async () => {
    try {
      if (!user?.id) return; // Don't fetch if there's no user

      const [appsResponse, transactionsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}apps/${user.id}`),
        fetch(`${API_BASE_URL}transactions/${user.id}`), // Updated endpoint
      ]);

      if (appsResponse.ok && transactionsResponse.ok) {
        const [appsData, transactionsData] = await Promise.all([
          appsResponse.json(),
          transactionsResponse.json(),
        ]);
        setApps(appsData);
        setTransactions(transactionsData);
        setEditedApps({});
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const totalCurrentAmount = useMemo(() => {
    return apps.reduce((sum, app) => sum + (app.currentAmount || 0), 0);
  }, [apps]);

  const handleCreateApp = async (e) => {
    e.preventDefault();
    console.log('user', user.id);
    console.log('newname', newAppName);
    console.log('newweb', newWebsite);
    try {
      const response = await fetch(`${API_BASE_URL}apps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          name: newAppName,
          website: newWebsite,
          currentAmount: 0,
        }),
      });

      if (response.ok) {
        setNewAppName("");
        setNewWebsite("");
        fetchData();
        setCurrentPage(1);
        setSearchTerm("");
      }
      console.log('handleCreateApp res', response);
    } catch (error) {
      console.error("Error creating app:", error);
    }
  };

  const handleSaveApps = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      // Process updates sequentially to maintain data consistency
      for (const [appId, changes] of Object.entries(editedApps)) {
        await fetch(`${API_BASE_URL}apps/${appId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(changes),
        });
      }

      await fetchData();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      setEditedApps({});
    } catch (error) {
      console.error("Error saving apps:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCellEdit = (appId, field, value) => {
    const parsedValue = ["minRedeem", "currentAmount"].includes(field)
      ? parseFloat(value) || 0
      : value;

    setEditedApps((prev) => ({
      ...prev,
      [appId]: {
        ...(prev[appId] || {}),
        [field]: parsedValue,
      },
    }));
  };

  // Calculate totals from transactions
  const appTotals = useMemo(() => {
    const totals = {};

    transactions.forEach((transaction) => {
      const appId = transaction.appId._id || transaction.appId;
      if (!totals[appId]) {
        totals[appId] = {
          totalDeposit: 0,
          totalWithdraw: 0,
        };
      }

      if (transaction.type === "deposit") {
        totals[appId].totalDeposit += Math.abs(transaction.amount);
      } else {
        totals[appId].totalWithdraw += Math.abs(transaction.amount);
      }
    });

    return totals;
  }, [transactions]);

  const filteredApps = useMemo(() => {
    return apps.filter(
      (app) =>
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.website.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [apps, searchTerm]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Special handling for calculated fields
      if (sortConfig.key === "netTotal") {
        const aTotal = appTotals[a._id] || {
          totalDeposit: 0,
          totalWithdraw: 0,
        };
        const bTotal = appTotals[b._id] || {
          totalDeposit: 0,
          totalWithdraw: 0,
        };
        aValue = aTotal.totalWithdraw - aTotal.totalDeposit;
        bValue = bTotal.totalWithdraw - bTotal.totalDeposit;
      } else if (sortConfig.key === "totalDeposit") {
        aValue = (appTotals[a._id] || { totalDeposit: 0 }).totalDeposit;
        bValue = (appTotals[b._id] || { totalDeposit: 0 }).totalDeposit;
      } else if (sortConfig.key === "totalWithdraw") {
        aValue = (appTotals[a._id] || { totalWithdraw: 0 }).totalWithdraw;
        bValue = (appTotals[b._id] || { totalWithdraw: 0 }).totalWithdraw;
      }

      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (columnKey) => {
    return (
      <ArrowUpDown
        className={`inline ml-2 h-4 w-4 ${
          sortConfig.key === columnKey ? "opacity-100" : "opacity-50"
        } cursor-pointer`}
      />
    );
  };

  // Update the filteredApps to include sorting
  const sortedAndFilteredApps = useMemo(() => {
    const filtered = apps.filter(
      (app) =>
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.website.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return getSortedData(filtered);
  }, [apps, searchTerm, sortConfig, appTotals]);

  // Update paginatedApps to use sortedAndFilteredApps
  const paginatedApps = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedAndFilteredApps.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedAndFilteredApps, currentPage]);

  const totalPages = Math.ceil(filteredApps.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const openAllWebsites = () => {
    filteredApps.forEach((app) => {
      const normalizedUrl =
        app.website.startsWith("http://") || app.website.startsWith("https://")
          ? app.website
          : `https://${app.website}`;
      window.open(normalizedUrl, "_blank");
    });
  };

  const handleOpenAlertModal = (app) => {
    setSelectedApp(app);
    setAlertType(app.alertType || "scheduled");
    setScheduledTime(app.fixedTime || "");
    setIntervalDuration(app.duration?.toString() || "");
    setIsAlertModalOpen(true);
  };

  const handleSaveAlert = async () => {
    const changes = {
      alertType,
      ...(alertType === "scheduled"
        ? { fixedTime: scheduledTime }
        : { duration: parseInt(intervalDuration) }),
    };

    await fetch(`${API_BASE_URL}apps/${selectedApp._id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(changes),
    });
    setIsAlertModalOpen(false);
  };

  const handleVerification = async (appId, currentValue) => {
    const newValue = !currentValue;

    try {
      await fetch(`${API_BASE_URL}apps/${appId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isVerified: newValue }),
      });

      // Update the local state to reflect the change
      setApps(
        apps.map((app) =>
          app._id === appId ? { ...app, isVerified: newValue } : app
        )
      );
    } catch (error) {
      console.error("Error updating verification status:", error);
    }
  };

  const handleArchive = async (appId) => {
    try {
      const response = await fetch(`${API_BASE_URL}apps/${appId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isActive: false }),
      });

      if (response.ok) {
        fetchData();
        setIsArchiveModalOpen(false);
      }
    } catch (error) {
      console.error("Error archiving app:", error);
    }
  };

  const openArchiveModal = (app) => {
    setAppToArchive(app);
    setIsArchiveModalOpen(true);
  };

  return (
    <Card style={{ backgroundColor: "#FFFFFF" }}>
      <CardHeader className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0 pb-4">
        <CardTitle>My Casinos</CardTitle>
        <div className="text-sm text-gray-600">
          Current $ in casino:{" "}
          <span className="font-bold">${totalCurrentAmount.toFixed(2)}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          onSubmit={handleCreateApp}
          className="flex flex-col sm:flex-row gap-2"
        >
          <input
            type="text"
            value={newAppName}
            onChange={(e) => setNewAppName(e.target.value)}
            placeholder="Enter name"
            className="flex-grow p-2 border rounded bg-white"
          />
          <input
            type="text"
            value={newWebsite}
            onChange={(e) => setNewWebsite(e.target.value)}
            placeholder="Enter website URL"
            className="flex-grow p-2 border rounded bg-white"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Add
          </button>
        </form>

        <div className="flex flex-col sm:flex-row items-center gap-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search by name"
            className="flex-grow p-2 border rounded bg-white"
          />
          <button
            onClick={openAllWebsites}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            disabled={filteredApps.length === 0}
          >
            Open All Websites
          </button>
          <button
            onClick={handleSaveApps}
            disabled={Object.keys(editedApps).length === 0 || isSaving}
            className={`
              px-4 py-2 bg-blue-500 text-white rounded flex items-center gap-2
              ${
                Object.keys(editedApps).length === 0 || isSaving
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-blue-600"
              }
              transition-colors
            `}
          >
            <span>Save Changes</span>
            {isSaving && (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            )}
            {saveSuccess && !isSaving && (
              <Check className="h-4 w-4 text-white" />
            )}
          </button>
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="p-2 sm:p-3 text-left font-medium">Action</th>
                <th
                  className="p-2 sm:p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("name")}
                >
                  Name {renderSortIcon("name")}
                </th>
                <th
                  className="p-2 sm:p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("currentAmount")}
                >
                  Current SC {renderSortIcon("currentAmount")}
                </th>
                <th
                  className="p-2 sm:p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("totalDeposit")}
                >
                  Total Deposit {renderSortIcon("totalDeposit")}
                </th>
                <th
                  className="p-2 sm:p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("totalWithdraw")}
                >
                  Total Withdraw {renderSortIcon("totalWithdraw")}
                </th>
                <th
                  className="p-2 sm:p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("netTotal")}
                >
                  Net Total {renderSortIcon("netTotal")}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedApps?.map((app) => {
                const normalizedUrl =
                  app.website.startsWith("http://") ||
                  app.website.startsWith("https://")
                    ? app.website
                    : `https://${app.website}`;

                const appTotal = appTotals[app._id] || {
                  totalDeposit: 0,
                  totalWithdraw: 0,
                };
                const netTotal = appTotal.totalWithdraw - appTotal.totalDeposit;

                return (
                  <tr key={app._id} className="border-t">
                    <td className="p-3">
                      <button
                        onClick={() => handleOpenAlertModal(app)}
                        className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                      >
                        <Bell className="h-4 w-4" />
                      </button>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <a
                          href={normalizedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          {app.name}
                        </a>
                        <button
                          onClick={() =>
                            handleVerification(app._id, app.isVerified)
                          }
                          className={`text-[0.6rem] px-1.5 py-0.5 border rounded ${
                            app.isVerified ? "text-green-500" : "text-gray-900"
                          } hover:opacity-80 transition-colors`}
                        >
                          {app.isVerified ? "[Verified]" : "[Verify]"}
                        </button>
                        <button
                          onClick={() => openArchiveModal(app)}
                          className="text-[0.6rem] px-1.5 py-0.5 border rounded text-red-500 hover:opacity-80 transition-colors"
                        >
                          [Archive]
                        </button>
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <input
                        type="number"
                        value={
                          editedApps[app._id]?.currentAmount !== undefined
                            ? editedApps[app._id].currentAmount
                            : app.currentAmount.toFixed(2) || 0
                        }
                        onChange={(e) =>
                          handleCellEdit(
                            app._id,
                            "currentAmount",
                            e.target.value
                          )
                        }
                        className="w-16 sm:w-24 border rounded text-right p-1"
                        step="0.01"
                      />
                    </td>
                    <td className="p-3 text-right text-red-600">
                      ${appTotal.totalDeposit.toFixed(2)}
                    </td>
                    <td className="p-3 text-right text-green-600">
                      ${appTotal.totalWithdraw.toFixed(2)}
                    </td>
                    <td className="p-3 text-right font-bold">
                      ${netTotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-sm text-gray-600">
            Total Casinos: {filteredApps.length}
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
            >
              Next
            </button>
          </div>
        </div>

        <Dialog open={isAlertModalOpen} onOpenChange={setIsAlertModalOpen}>
          <DialogContent style={{ backgroundColor: "#FFFFFF" }}>
            <DialogHeader>
              <DialogTitle>Set Alert for {selectedApp?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-base font-semibold">Alert Type</Label>
                <RadioGroup
                  value={alertType}
                  onValueChange={setAlertType}
                  className="flex flex-col space-y-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="scheduled" id="scheduled" />
                    <Label htmlFor="scheduled">Scheduled</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="interval-based"
                      id="interval-based"
                    />
                    <Label htmlFor="interval-based">Interval-Based</Label>
                  </div>
                </RadioGroup>
              </div>

              {alertType === "scheduled" && (
                <div className="space-y-2">
                  <Label htmlFor="scheduledTime">Schedule Time</Label>
                  <Input
                    id="scheduledTime"
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                  />
                </div>
              )}

              {alertType === "interval-based" && (
                <div className="space-y-2">
                  <Label htmlFor="intervalDuration">Interval (hours)</Label>
                  <Input
                    id="intervalDuration"
                    type="number"
                    min="1"
                    value={intervalDuration}
                    onChange={(e) => setIntervalDuration(e.target.value)}
                  />
                </div>
              )}

              <button
                onClick={handleSaveAlert}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Save Alert
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Archive Confirmation Dialog */}
        <Dialog open={isArchiveModalOpen} onOpenChange={setIsArchiveModalOpen}>
          <DialogContent style={{ backgroundColor: "#FFFFFF" }}>
            <DialogHeader>
              <DialogTitle>Archive {appToArchive?.name}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-700">
                Are you sure you want to archive <span className="font-semibold">{appToArchive?.name}</span>? This will hide it from your casino list and alerts but you can restore it later if needed.
              </p>
            </div>
            <DialogFooter className="flex justify-end space-x-2">
              <button
                onClick={() => setIsArchiveModalOpen(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleArchive(appToArchive._id)}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Archive
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AppListComponents;
